// Dashboard.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./dashboard.css";
import course1 from "../assets/8.jpg";
import course2 from "../assets/2.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  getCourses,
  getCourseEnrollments,
  getActivityLogs,
  getAdminDashboardMetrics,
  getEnrolmentsByLga,
  downloadDauUsers,
  downloadWauUsers,
  downloadMauUsers,
  downloadInactiveDailyUsers,
  downloadInactiveWeeklyUsers,
  downloadInactiveMonthlyUsers,
  downloadCompletionRateUsers,
  downloadTotalEnrollments,
  downloadCompletedEnrollments,
} from "../api";
import logger from "../logger";
import * as XLSX from "xlsx";

const API_BASE_URL = "https://care-api.equily.ng/api";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [enrolmentLgaData, setEnrolmentLgaData] = useState([]);
  const [selectedLgaCourse, setSelectedLgaCourse] = useState("");
  const [filterType, setFilterType] = useState("year"); // "year" | "date"
  const [selectedYear, setSelectedYear] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const dateFilters = useMemo(() => {
    // Backend applies date filtering only when both bounds are present.
    if (dateFrom && dateTo) {
      return { from: dateFrom, to: dateTo };
    }
    return {};
  }, [dateFrom, dateTo]);

  const YEAR_RANGES = [
    { label: "Year 1", from: "2025-01-01", to: "2026-05-30" },
    { label: "Year 2", from: "2026-06-01", to: "2026-12-31" },
    { label: "Year 3", from: "2027-01-01", to: "2027-12-31" },
  ];
  const [stats, setStats] = useState({
    totalLearners: 0,
    totalCourses: 0,
    totalEnrollments: 0,
    averageScore: 0,
    completedCourses: 0,
    activeCourses: 0,
    dailyActiveUsers: 0,
    weeklyActiveUsers: 0,
    monthlyActiveUsers: 0,
    completionRate: 0,
    completionRateCompletedUsers: 0,
    completionRateEligibleUsers: 0,
  });

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        // Fetch metrics, courses, and LGA distribution in parallel.
        // Cadre + gender + total_learners are now aggregated server-side
        // inside getAdminDashboardMetrics, so we no longer pull a large
        // learners sample to compute them client-side.
        const [coursesRes, metricsRes, enrolmentsByLgaRes] = await Promise.all([
          getCourses({ limit: 100, ...dateFilters }),
          getAdminDashboardMetrics(dateFilters),
          getEnrolmentsByLga(
            selectedLgaCourse ? { course_id: selectedLgaCourse } : {},
            dateFilters,
          ),
        ]);

        if (coursesRes) setCourses(coursesRes);
        if (metricsRes) setData(metricsRes);
        if (enrolmentsByLgaRes?.data) {
          setEnrolmentLgaData(
            enrolmentsByLgaRes.data
              .map((item) => ({
                name: (item.lga_name || "Unknown").substring(0, 20),
                fullName: item.lga_name || "Unknown",
                value: Number(item.enrolments_count || 0),
              }))
              .sort((a, b) => b.value - a.value),
          );
        }

        // Process data
        if (metricsRes) {
          // Use metrics from backend where available
          const totalLearners =
            metricsRes.total_learners ??
            (metricsRes.top_staff ? metricsRes.top_staff.length : 0);
          const totalCourses =
            metricsRes.total_courses || (coursesRes ? coursesRes.length : 0);
          const totalEnrollments = metricsRes.enrollments || 0;
          const completedCourses = metricsRes.completed_courses || 0;

          // Use counts from metrics or fallback to courses length
          const activeCourses = metricsRes.course_status
            ? metricsRes.course_status.filter(
                (c) => c.status === "published" || c.is_published,
              ).length
            : coursesRes
              ? coursesRes.filter((c) => c.is_published && !c.is_archived)
                  .length
              : 0;

          // Process DAU/WAU/MAU from admin metrics
          const dailyActiveUsers = metricsRes.dau_active || 0;
          const weeklyActiveUsers = metricsRes.wau_active || 0;
          const monthlyActiveUsers = metricsRes.mau_active || 0;

          // Average score directly from metrics
          const averageScore = metricsRes.average_score || 0;

          const completionRateData = metricsRes.completion_rate || {};

          setStats({
            totalLearners,
            totalCourses,
            totalEnrollments,
            averageScore: parseFloat(averageScore || 0).toFixed(1),
            completedCourses,
            activeCourses,
            dailyActiveUsers,
            weeklyActiveUsers,
            monthlyActiveUsers,
            completionRate: completionRateData.rate || 0,
            completionRateCompletedUsers: completionRateData.completed_users || 0,
            completionRateEligibleUsers: completionRateData.eligible_users || 0,
          });

          // Background fetching for enrollments (optional/non-blocking)
          (async () => {
            // simplified background fetch if needed,
            // but omitting complex activity log fetching for now as we use backend metrics
          })();

          setLoading(false);
        }
      } catch (err) {
        logger.error("Failed to fetch dashboard data:", err);
        setLoading(false);
      }
    };

    // Background refresh function - updates stats without showing loading state
    const refreshDashboardStats = async () => {
      try {
        // Fetch data silently in the background
        const [coursesRes, metricsRes, enrolmentsByLgaRes] = await Promise.all([
          getCourses({ limit: 100, ...dateFilters }),
          getAdminDashboardMetrics(dateFilters),
          getEnrolmentsByLga(
            selectedLgaCourse ? { course_id: selectedLgaCourse } : {},
            dateFilters,
          ),
        ]);

        if (coursesRes) setCourses(coursesRes);
        if (metricsRes) setData(metricsRes);
        if (enrolmentsByLgaRes?.data) {
          setEnrolmentLgaData(
            enrolmentsByLgaRes.data
              .map((item) => ({
                name: (item.lga_name || "Unknown").substring(0, 20),
                fullName: item.lga_name || "Unknown",
                value: Number(item.enrolments_count || 0),
              }))
              .sort((a, b) => b.value - a.value),
          );
        }

        // Process data
        if (metricsRes) {
          const totalLearners =
            metricsRes.total_learners ??
            (metricsRes.top_staff ? metricsRes.top_staff.length : 0);
          const totalCourses =
            metricsRes.total_courses || (coursesRes ? coursesRes.length : 0);
          const totalEnrollments = metricsRes.enrollments || 0;
          const completedCourses = metricsRes.completed_courses || 0;

          const activeCourses = metricsRes.course_status
            ? metricsRes.course_status.filter(
                (c) => c.status === "published" || c.is_published,
              ).length
            : coursesRes
              ? coursesRes.filter((c) => c.is_published && !c.is_archived)
                  .length
              : 0;

          const dailyActiveUsers = metricsRes.dau_active || 0;
          const weeklyActiveUsers = metricsRes.wau_active || 0;
          const monthlyActiveUsers = metricsRes.mau_active || 0;
          const averageScore = metricsRes.average_score || 0;
          const completionRateData = metricsRes.completion_rate || {};

          setStats({
            totalLearners,
            totalCourses,
            totalEnrollments,
            averageScore: parseFloat(averageScore || 0).toFixed(1),
            completedCourses,
            activeCourses,
            dailyActiveUsers,
            weeklyActiveUsers,
            monthlyActiveUsers,
            completionRate: completionRateData.rate || 0,
            completionRateCompletedUsers: completionRateData.completed_users || 0,
            completionRateEligibleUsers: completionRateData.eligible_users || 0,
          });
        }
      } catch (err) {
        // Silently handle errors during background refresh
        logger.error("Background refresh error:", err);
      }
    };

    // Initial load with loading state
    fetchDashboard();

    // Auto-refresh every 2 minutes in background without loading state
    const interval = setInterval(() => {
      refreshDashboardStats();
    }, 120000);

    return () => clearInterval(interval);
  }, [selectedLgaCourse, dateFilters]);

  // Skeleton loading component
  // Reconstruct lightweight meMetrics from stats and data to satisfy JSX
  const meMetrics = {
    totalEnrolledUsers: stats.totalLearners,
    activeUsers: stats.dailyActiveUsers,
    inactiveUsers: Math.max(0, stats.totalLearners - stats.dailyActiveUsers),
    suspendedUsers: 0,
    activePercentage:
      stats.totalLearners > 0
        ? ((stats.dailyActiveUsers / stats.totalLearners) * 100).toFixed(1)
        : 0,
    inactivePercentage:
      stats.totalLearners > 0
        ? (
            ((stats.totalLearners - stats.dailyActiveUsers) /
              stats.totalLearners) *
            100
          ).toFixed(1)
        : 0,
    suspendedPercentage: 0,
    totalCourseEnrollments: stats.totalEnrollments,
    overallCompletionRate:
      stats.totalEnrollments > 0
        ? ((stats.completedCourses / stats.totalEnrollments) * 100).toFixed(1)
        : 0,
    engagementRate:
      stats.totalLearners > 0
        ? ((stats.dailyActiveUsers / stats.totalLearners) * 100).toFixed(1)
        : 0,
    avgEnrollmentsPerUser:
      stats.totalLearners > 0
        ? (stats.totalEnrollments / stats.totalLearners).toFixed(1)
        : 0,
    totalCompleted: stats.completedCourses,

    // Fallback empty arrays for charts if data not available
    facilityData: [],
    lgaData: [],
    staffLgaData: [],
    cadreData: [],
    completionTrends: [],
    courseEnrollments: [],
    enrollmentCourseNames: [],
    enrollmentCourseColors: [],
    topPerformingCourses: [],
    demographics: {
      female: 0,
      male: 0,
      femalePercentage: 0,
      malePercentage: 0,
    },
  };

  // Populate chart data if we have top_staff or course data
  if (data && data.top_staff) {
    // Use top_staff for facility/LGA aggregation (LGA is also overridden
    // below by the dedicated enrolments-by-LGA endpoint when available).
    const chartSourceData = data.top_staff;

    const resolveFacilityName = (entity = {}) => {
      // Try entity.facility object first
      if (entity.facility && typeof entity.facility === "object")
        return entity.facility.name || "N/A";

      // Try direct string properties
      if (entity.facility_name) return entity.facility_name;
      if (entity.facilityName) return entity.facilityName;
      if (entity.primary_facility_name) return entity.primary_facility_name;
      if (entity.centre_name) return entity.centre_name;
      if (entity.center_name) return entity.center_name;

      // Fallback to ID if present
      if (entity.facility_id) return `Facility #${entity.facility_id}`;
      if (entity.facilityId) return `Facility #${entity.facilityId}`;

      return "N/A";
    };

    // Facilities from top_staff sample
    const facilityCounts = {};
    data.top_staff.forEach((s) => {
      const name = resolveFacilityName(s);
      if (name && name !== "N/A")
        facilityCounts[name] = (facilityCounts[name] || 0) + 1;
    });
    meMetrics.facilityData = Object.entries(facilityCounts)
      .map(([name, value]) => ({
        name: name.substring(0, 20),
        fullName: name,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const resolveLgaName = (entity = {}) => {
      if (entity.lga && typeof entity.lga === "object")
        return entity.lga.name || "N/A";
      if (entity.lga_name) return entity.lga_name;
      if (entity.lga_id) return `LGA #${entity.lga_id}`;
      return "N/A";
    };

    // Staff distribution by LGA from top_staff sample (fallback)
    const lgaCounts = {};
    chartSourceData.forEach((s) => {
      const name = resolveLgaName(s);
      if (name && name !== "N/A") lgaCounts[name] = (lgaCounts[name] || 0) + 1;
    });
    meMetrics.staffLgaData = Object.entries(lgaCounts)
      .map(([name, value]) => ({
        name: name.substring(0, 20),
        fullName: name,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  }

  // Staff by LGA: prefer backend aggregate (covers ALL staff)
  if (
    data &&
    Array.isArray(data.staff_lga_distribution) &&
    data.staff_lga_distribution.length > 0
  ) {
    meMetrics.staffLgaData = data.staff_lga_distribution
      .map((row) => ({
        name: (row.name || "Unknown").substring(0, 20),
        fullName: row.name || "Unknown",
        value: Number(row.value || 0),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  }

  // Cadre distribution: prefer backend aggregate (covers ALL staff)
  if (data && Array.isArray(data.cadre_distribution) && data.cadre_distribution.length > 0) {
    meMetrics.cadreData = data.cadre_distribution
      .map((row) => ({
        name: row.name || "Unknown",
        value: Number(row.value || 0),
      }))
      .filter((i) => i.name && i.name !== "Unknown")
      .sort((a, b) => b.value - a.value);
  } else if (data && data.top_staff) {
    // Fallback to top_staff sample
    const cadreCounts = {};
    data.top_staff.forEach((s) => {
      let cadre = "Unknown";
      if (s.cadre?.name) cadre = s.cadre.name;
      else if (s.jobtitle?.name) cadre = s.jobtitle.name;
      else if (s.job_title_name) cadre = s.job_title_name;
      cadreCounts[cadre] = (cadreCounts[cadre] || 0) + 1;
    });
    meMetrics.cadreData = Object.entries(cadreCounts)
      .map(([name, value]) => ({ name, value }))
      .filter((i) => i.name !== "Unknown")
      .sort((a, b) => b.value - a.value);
  }

  // Demographics: prefer backend aggregate (covers ALL staff)
  if (data && data.gender_distribution) {
    const gd = data.gender_distribution;
    meMetrics.demographics = {
      female: Number(gd.female || 0),
      male: Number(gd.male || 0),
      femalePercentage: Number(gd.female_percentage || 0),
      malePercentage: Number(gd.male_percentage || 0),
    };
  } else if (data && data.top_staff) {
    // Fallback to top_staff sample
    const femaleCount = data.top_staff.filter((l) =>
      (l.gender || "").toLowerCase().startsWith("f"),
    ).length;
    const maleCount = data.top_staff.filter((l) =>
      (l.gender || "").toLowerCase().startsWith("m"),
    ).length;
    const totalWithGender = femaleCount + maleCount;

    meMetrics.demographics = {
      female: femaleCount,
      male: maleCount,
      femalePercentage:
        totalWithGender > 0
          ? ((femaleCount / totalWithGender) * 100).toFixed(1)
          : 0,
      malePercentage:
        totalWithGender > 0
          ? ((maleCount / totalWithGender) * 100).toFixed(1)
          : 0,
    };
  }

  if (enrolmentLgaData.length > 0) {
    meMetrics.lgaData = enrolmentLgaData;
  } else {
    // Fallback so chart still renders when enrolment endpoint is unavailable.
    meMetrics.lgaData = meMetrics.staffLgaData;
  }

  // Populate course metrics
  if (courses.length > 0) {
    const courseEnrollments = courses.map((course) => {
      const enrolled = course.learners_count || 0;
      const completed = course.completed || 0;
      return {
        name: course.title || "Untitled",
        enrolled,
        completionRate:
          enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0,
        incompleteRate:
          enrolled > 0 ? 100 - Math.round((completed / enrolled) * 100) : 100,
        percentageOfTotal:
          stats.totalEnrollments > 0
            ? ((enrolled / stats.totalEnrollments) * 100).toFixed(1)
            : 0,
      };
    });
    meMetrics.courseEnrollments = courseEnrollments;
    meMetrics.topPerformingCourses = [...courseEnrollments]
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 5);
  }

  const SkeletonCard = () => (
    <div className="stat-card skeleton-card">
      <div className="skeleton-line skeleton-title"></div>
      <div className="skeleton-line skeleton-value"></div>
      <div className="skeleton-line skeleton-subtitle"></div>
    </div>
  );

  const SkeletonChart = () => (
    <div className="card skeleton-chart">
      <div className="skeleton-line skeleton-chart-title"></div>
      <div className="skeleton-chart-content"></div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <div className="header-content">
            <div className="skeleton-line skeleton-header-title"></div>
            <div className="skeleton-line skeleton-header-subtitle"></div>
          </div>
        </div>
        <div className="stats-grid">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="cards-grid">
          {[...Array(4)].map((_, i) => (
            <SkeletonChart key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dashboard-page">
        <div className="error-container">
          <h2>Failed to load dashboard data.</h2>
          <p>Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // 🔹 Sort top courses: highest enrollments + score (using live courses data)
  const sortedTopCourses = [...(courses || [])]
    .map((course) => ({
      ...course,
      enrollments: course.learners_count || 0,
      avg_score: course.tests_avg_percentile || 0,
    }))
    .sort((a, b) => {
      const aMetric = (a.enrollments || 0) + (a.avg_score || 0);
      const bMetric = (b.enrollments || 0) + (b.avg_score || 0);
      return bMetric - aMetric;
    })
    .slice(0, 10);

  // Most enrolled courses (sorted by learners_count)
  const mostEnrolledCourses = [...(courses || [])]
    .map((course) => ({
      ...course,
      enrollments: course.learners_count || 0,
    }))
    .sort((a, b) => (b.enrollments || 0) - (a.enrollments || 0))
    .slice(0, 10);

  // Least enrolled courses (sorted by learners_count ascending)
  const leastEnrolledCourses = [...(courses || [])]
    .map((course) => ({
      ...course,
      enrollments: course.learners_count || 0,
    }))
    .sort((a, b) => (a.enrollments || 0) - (b.enrollments || 0))
    .slice(0, 10);

  // Calculate additional metrics
  const learningTime = data.learning_time || 0;
  const completedCourses =
    data.completed_courses || stats.completedCourses || 0;
  const averageScore = parseFloat(data.average_score || stats.averageScore || 0);
  const totalEnrollments = data.enrollments || stats.totalEnrollments || 0;
  const totalLearners = stats.totalLearners || data.total_learners || 0;
  const totalCourses = stats.totalCourses || data.total_courses || 0;
  const activeCourses = stats.activeCourses || 0;

  const sanitizeSheetName = (name) =>
    (name || "Sheet")
      .replace(/[[\\/?*:\]]/g, "")
      .slice(0, 31)
      .trim() || "Sheet";

  const appendSheet = (workbook, name, rows) => {
    const safeRows = rows?.length ? rows : [{ Info: "No data available" }];
    const worksheet = XLSX.utils.json_to_sheet(safeRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(name));
  };

  const handleExportAnalyticsExcel = () => {
    const workbook = XLSX.utils.book_new();

    const analyticsCardsRows = [
      { Metric: "Total FLWs", Value: meMetrics.totalEnrolledUsers || 0 },
      { Metric: "Daily Active Users", Value: stats.dailyActiveUsers || 0 },
      {
        Metric: "Daily Active Users (%)",
        Value:
          meMetrics.totalEnrolledUsers > 0
            ? Number(
                (
                  ((stats.dailyActiveUsers || 0) / meMetrics.totalEnrolledUsers) *
                  100
                ).toFixed(1),
              )
            : 0,
      },
      { Metric: "Monthly Active Users", Value: stats.monthlyActiveUsers || 0 },
      {
        Metric: "Monthly Active Users (%)",
        Value:
          meMetrics.totalEnrolledUsers > 0
            ? Number(
                (
                  ((stats.monthlyActiveUsers || 0) /
                    meMetrics.totalEnrolledUsers) *
                  100
                ).toFixed(1),
              )
            : 0,
      },
      { Metric: "Total Enrollments", Value: totalEnrollments || 0 },
      { Metric: "Total Courses", Value: totalCourses || 0 },
      { Metric: "Enrollments Completed", Value: completedCourses || 0 },
      { Metric: "Average Score (%)", Value: Number(averageScore.toFixed(1)) },
      {
        Metric: "Inactive Users (Daily)",
        Value: Math.max(0, (stats.totalLearners || 0) - (stats.dailyActiveUsers || 0)),
      },
      {
        Metric: "Inactive Users (Monthly)",
        Value: Math.max(
          0,
          (stats.totalLearners || 0) - (stats.monthlyActiveUsers || 0),
        ),
      },
      { Metric: "Suspended Users", Value: meMetrics.suspendedUsers || 0 },
    ];

    const coursePerformanceRows = (data.course_status || []).map((course) => {
      const enrolled = Number(course.learners_count || 0);
      const completed = Number(course.completed || 0);
      const completionRate = enrolled > 0 ? (completed / enrolled) * 100 : 0;

      return {
        Course: course.title || "Untitled",
        Enrolled: enrolled,
        Completed: completed,
        Pending: Number(course.pending || 0),
        "Avg Score (%)": course.tests_avg_percentile
          ? Number(Number(course.tests_avg_percentile).toFixed(1))
          : "N/A",
        "Completion Rate (%)": Number(completionRate.toFixed(1)),
      };
    });

    const topWorkersRows = (data.top_staff || []).slice(0, 10).map((staff, index) => ({
      Rank: index + 1,
      "First Name": staff.firstname || "",
      "Last Name": staff.lastname || "",
      "Full Name": `${staff.firstname || ""} ${staff.lastname || ""}`.trim(),
      Cadre: staff.jobtitle?.name || staff.job_title_name || "N/A",
      Facility:
        staff.facility?.name ||
        staff.facility_name ||
        staff.facilityName ||
        staff.primary_facility_name ||
        "N/A",
      LGA: staff.lga?.name || staff.lga_name || "N/A",
      Gender: staff.gender || "N/A",
    }));

    const topCoursesRows = sortedTopCourses.map((course, index) => ({
      Rank: index + 1,
      Course: course.title || "Untitled",
      Enrollments: Number(course.enrollments || 0),
      "Average Score (%)": course.avg_score
        ? Number(Number(course.avg_score).toFixed(1))
        : "N/A",
      "Course ID": course.id || "N/A",
    }));

    const leastEnrolledRows = leastEnrolledCourses.map((course, index) => ({
      Rank: index + 1,
      Course: course.title || "Untitled",
      Enrollments: Number(course.enrollments || 0),
      "Course ID": course.id || "N/A",
    }));

    const lgaRows = (meMetrics.lgaData || []).map((item, index) => ({
      Rank: index + 1,
      LGA: item.fullName || item.name || "Unknown",
      Enrollments: Number(item.value || 0),
    }));

    const staffLgaRows = (meMetrics.staffLgaData || []).map((item, index) => ({
      Rank: index + 1,
      LGA: item.fullName || item.name || "Unknown",
      Staff: Number(item.value || 0),
    }));

    const cadreRows = (meMetrics.cadreData || []).map((item, index) => ({
      Rank: index + 1,
      Cadre: item.name || "Unknown",
      Users: Number(item.value || 0),
    }));

    const userActivityRows = [
      {
        Segment: "Active Users",
        Users: Number(meMetrics.activeUsers || 0),
        "Percentage (%)": Number(meMetrics.activePercentage || 0),
      },
      {
        Segment: "Inactive Users",
        Users: Number(meMetrics.inactiveUsers || 0),
        "Percentage (%)": Number(meMetrics.inactivePercentage || 0),
      },
      {
        Segment: "Suspended Users",
        Users: Number(meMetrics.suspendedUsers || 0),
        "Percentage (%)": Number(meMetrics.suspendedPercentage || 0),
      },
    ];

    const demographicsRows = [
      {
        Gender: "Female",
        Users: Number(meMetrics.demographics.female || 0),
        "Percentage (%)": Number(meMetrics.demographics.femalePercentage || 0),
      },
      {
        Gender: "Male",
        Users: Number(meMetrics.demographics.male || 0),
        "Percentage (%)": Number(meMetrics.demographics.malePercentage || 0),
      },
    ];

    appendSheet(workbook, "Analytics Cards", analyticsCardsRows);
    appendSheet(workbook, "Course Performance", coursePerformanceRows);
    appendSheet(workbook, "Top Workers", topWorkersRows);
    appendSheet(workbook, "Top Courses", topCoursesRows);
    appendSheet(workbook, "Least Enrolled", leastEnrolledRows);
    appendSheet(workbook, "Users by LGA", lgaRows);
    appendSheet(workbook, "Staff by LGA", staffLgaRows);
    appendSheet(workbook, "Users by Cadre", cadreRows);
    appendSheet(workbook, "User Activity", userActivityRows);
    appendSheet(workbook, "Demographics", demographicsRows);

    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    XLSX.writeFile(workbook, `Dashboard_Analytics_${stamp}.xlsx`);
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard Overview</h1>
          <p className="header-subtitle">
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        <div className="header-controls">
          {/* Filter type toggle */}
          <div className="filter-type-toggle">
            <span className="filter-type-label">Filter by:</span>
            <button
              className={`filter-type-btn${filterType === "year" ? " active" : ""}`}
              onClick={() => {
                setFilterType("year");
                setDateFrom("");
                setDateTo("");
                setSelectedYear("");
              }}
            >
              A: Enrollment Year
            </button>
            <button
              className={`filter-type-btn${filterType === "date" ? " active" : ""}`}
              onClick={() => {
                setFilterType("date");
                setDateFrom("");
                setDateTo("");
                setSelectedYear("");
              }}
            >
              B: Custom Date
            </button>
          </div>

          {filterType === "year" ? (
            <div className="dashboard-date-filter">
              {YEAR_RANGES.map((yr) => (
                <button
                  key={yr.label}
                  className={`year-filter-btn${selectedYear === yr.label ? " active" : ""}`}
                  onClick={() => {
                    setSelectedYear(yr.label);
                    setDateFrom(yr.from);
                    setDateTo(yr.to);
                  }}
                >
                  {yr.label}
                  <span className="year-filter-range">
                    {yr.from.slice(0, 7)} – {yr.to.slice(0, 7)}
                  </span>
                </button>
              ))}
              {selectedYear && (
                <button
                  className="date-filter-clear"
                  onClick={() => {
                    setSelectedYear("");
                    setDateFrom("");
                    setDateTo("");
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          ) : (
            <div className="dashboard-date-filter">
              <label className="date-filter-label">From</label>
              <input
                type="date"
                className="dashboard-date-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <label className="date-filter-label">To</label>
              <input
                type="date"
                className="dashboard-date-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
              {(dateFrom || dateTo) && (
                <button
                  className="date-filter-clear"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          <div className="header-date">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>

          <button
            className="export-analytics-btn"
            onClick={handleExportAnalyticsExcel}
            type="button"
          >
            Export Analytics (Excel)
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="stats-grid">
        {/* Total FLWs - All registered FLWs */}
        <div className="stat-card stat-card-warning">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Total FLWs</div>
              <div className="stat-value">
                {meMetrics.totalEnrolledUsers.toLocaleString()}
              </div>
              <div className="stat-change">
                <span>All registered FLWs on the platform</span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Active FLWs */}
        <div className="stat-card stat-card-success">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Daily Active Users</div>
              <div className="stat-value">
                {stats.dailyActiveUsers.toLocaleString()}
              </div>
              <div className="stat-change positive">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 14l5-5 5 5z" />
                </svg>
                {meMetrics.totalEnrolledUsers > 0
                  ? (
                      (stats.dailyActiveUsers / meMetrics.totalEnrolledUsers) *
                      100
                    ).toFixed(1)
                  : 0}
                % of total users
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download DAU list as CSV"
            onClick={() => downloadDauUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Weekly Active Users */}
        <div className="stat-card stat-card-info">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Weekly Active Users</div>
              <div className="stat-value">
                {stats.weeklyActiveUsers.toLocaleString()}
              </div>
              <div className="stat-change positive">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 14l5-5 5 5z" />
                </svg>
                {meMetrics.totalEnrolledUsers > 0
                  ? (
                      (stats.weeklyActiveUsers / meMetrics.totalEnrolledUsers) *
                      100
                    ).toFixed(1)
                  : 0}
                % of total users
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download WAU list as CSV"
            onClick={() => downloadWauUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Monthly Active Users */}
        <div className="stat-card stat-card-warning">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="dash-info">
              <div className="stat-label">Monthly Active Users</div>
              <div className="stat-value">
                {stats.monthlyActiveUsers.toLocaleString()}
              </div>
              <div className="stat-change positive">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 14l5-5 5 5z" />
                </svg>
                {meMetrics.totalEnrolledUsers > 0
                  ? (
                      (stats.monthlyActiveUsers /
                        meMetrics.totalEnrolledUsers) *
                      100
                    ).toFixed(1)
                  : 0}
                % of total users
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download MAU list as CSV"
            onClick={() => downloadMauUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Total Enrollments - All course enrollments */}
        <div className="stat-card stat-card-info">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Enrollments</div>
              <div className="stat-value">
                {totalEnrollments.toLocaleString()}
              </div>
              <div className="stat-change">
                <span>All course enrollments across platform</span>
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download total enrollments as CSV"
            onClick={() => downloadTotalEnrollments(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Total Courses */}
        <div className="stat-card stat-card-secondary">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Courses</div>
              <div className="stat-value">{totalCourses.toLocaleString()}</div>
              <div className="stat-change">
                <span>10 active</span>
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Enrollments Completed</div>
              <div className="stat-value">
                {completedCourses.toLocaleString()}
              </div>
              <div className="stat-change positive">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 14l5-5 5 5z" />
                </svg>
                Completed
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download completed enrollments as CSV"
            onClick={() => downloadCompletedEnrollments(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Average Score</div>
              <div className="stat-value">{averageScore.toFixed(1)}%</div>
              <div className="stat-change">
                <span>Overall performance</span>
              </div>
            </div>
          </div>
        </div>

        {/* Inactive Daily Users */}
        <div className="stat-card stat-card-warning">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Inactive Users (Daily)</div>
              <div className="stat-value">
                {(
                  stats.totalLearners - stats.dailyActiveUsers
                ).toLocaleString()}
              </div>
              <div className="stat-change">
                <span>Not active in last 24h</span>
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download Inactive Daily Users list as CSV"
            onClick={() => downloadInactiveDailyUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Inactive Weekly Users */}
        <div className="stat-card stat-card-warning">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Inactive Users (Weekly)</div>
              <div className="stat-value">
                {(
                  stats.totalLearners - stats.weeklyActiveUsers
                ).toLocaleString()}
              </div>
              <div className="stat-change">
                <span>Not active in last 7 days</span>
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download Inactive Weekly Users list as CSV"
            onClick={() => downloadInactiveWeeklyUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Inactive Monthly Users */}
        <div className="stat-card stat-card-info">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Inactive Users (Monthly)</div>
              <div className="stat-value">
                {(
                  stats.totalLearners - stats.monthlyActiveUsers
                ).toLocaleString()}
              </div>
              <div className="stat-change">
                <span>Not active in last 30 days</span>
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download Inactive Monthly Users list as CSV"
            onClick={() => downloadInactiveMonthlyUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Completion Rate */}
        <div className="stat-card stat-card-success">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Completion Rate</div>
              <div className="stat-value">{stats.completionRate}%</div>
              <div className="stat-change">
                <span>
                  {stats.completionRateCompletedUsers.toLocaleString()} of{" "}
                  {stats.completionRateEligibleUsers.toLocaleString()} eligible users
                  completed all their courses
                </span>
              </div>
            </div>
          </div>
          <button
            className="stat-card-download-btn"
            title="Download Completion Rate list as CSV"
            onClick={() => downloadCompletionRateUsers(dateFilters)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            CSV
          </button>
        </div>

        {/* Suspended Users */}
        <div className="stat-card stat-card-danger">
          <div className="stat-content">
            <div className="stat-icon-wrapper">
              <svg
                className="stat-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <div className="stat-info">
              <div className="stat-label">Suspended Users</div>
              <div className="stat-value">
                {meMetrics.suspendedUsers.toLocaleString()}
              </div>
              <div className="stat-change">
                <span>{meMetrics.suspendedPercentage}% of total users</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Performance Table + Chart */}
      <div className="card performance-card">
        <div className="card-header-with-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3>Course Performance</h3>
        </div>
        <div className="performance-container">
          {/* Table */}
          {data.course_status?.length > 0 ? (
            <div className="table-wrapper">
              <table className="status-table">
                <thead>
                  <tr>
                    <th>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      Course
                    </th>
                    <th>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                      Enrolled
                    </th>
                    <th>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                      </svg>
                      Completed
                    </th>
                    <th>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      Avg Score
                    </th>
                    <th>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                      Completion Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.course_status.map((course, index) => {
                    const courseMe = meMetrics.courseEnrollments.find(
                      (c) => c.name === course.title,
                    );
                    const enrolled =
                      courseMe?.enrolled || course.learners_count || 0;
                    const completed = course.completed || 0;
                    const completionRate =
                      enrolled > 0
                        ? Math.round((completed / enrolled) * 100)
                        : 0;
                    const isLowCompletion = completionRate < 60;
                    const isHighCompletion = completionRate >= 75;
                    return (
                      <tr key={course.id || index} className="table-row">
                        <td className="course-name-cell">
                          <strong>{course.title}</strong>
                        </td>
                        <td>
                          <span className="badge badge-info">{enrolled}</span>
                        </td>
                        <td>
                          <span className="badge badge-success">
                            {course.completed || 0}
                          </span>
                        </td>
                        <td>
                          <span className="score-badge">
                            {course.tests_avg_percentile
                              ? `${course.tests_avg_percentile.toFixed(1)}%`
                              : "N/A"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${isLowCompletion ? "badge-danger" : isHighCompletion ? "badge-success" : "badge-warning"}`}
                          >
                            {completionRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p>No course performance data available.</p>
            </div>
          )}

          <div className="card-header-with-icon" style={{ marginTop: "24px" }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="mt-4">Enrollment Performance (Completed Vs Pending)</h3>
          </div>

          {/* Chart */}
          {data.course_status?.length > 0 && (
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={data.course_status}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="title"
                    tick={{ fill: "#6c757d", fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={150}
                  />
                  <YAxis tick={{ fill: "#6c757d", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="pending"
                    fill="#f59e0b"
                    name="Pending"
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="completed"
                    fill="#10b981"
                    name="Completed"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Top Health Workers */}
      <div className="card top-workers-card">
        <div className="card-header-with-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3>Top Health Workers</h3>
        </div>
        {data.top_staff?.length > 0 ? (
          <div className="workers-list">
            {data.top_staff.slice(0, 10).map((staff, i) => (
              <div key={i} className="worker-item">
                <div className="worker-rank">{i + 1}</div>
                <div className="worker-avatar">
                  <span>
                    {(staff.firstname?.[0] || "U") +
                      (staff.lastname?.[0] || "")}
                  </span>
                </div>
                <div className="worker-details">
                  <p className="worker-name">
                    {staff.firstname} {staff.lastname}
                  </p>
                  {staff.jobtitle?.name && (
                    <span className="worker-role">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                      {staff.jobtitle.name}
                    </span>
                  )}
                </div>
                {i < 3 && <div className="top-badge">Top {i + 1}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p>No top health workers yet.</p>
          </div>
        )}
      </div>

      {/* Courses */}
      <div className="cards-grid">
        <div className="most-enrolled-card course-list-card">
          <div className="card-header-with-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
            <h3>Top Courses</h3>
          </div>
          {sortedTopCourses.length > 0 ? (
            <div className="courses-list">
              {sortedTopCourses.map((course, i) => (
                <div key={course.id || i} className="course-item">
                  <div className="course-rank">{i + 1}</div>
                  <img
                    src={course.image_url || course.image || course1}
                    alt={course.title}
                  />
                  <div className="course-details">
                    <p className="course-title">{course.title}</p>
                    <div className="course-metrics">
                      <span className="metric-badge">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        {course.enrollments || 0} enrolled
                      </span>
                      {course.avg_score && (
                        <span className="metric-badge score">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          {course.avg_score.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No top courses available.</p>
            </div>
          )}
        </div>

        <div className="most-enrolled-card course-list-card">
          <div className="card-header-with-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
              />
            </svg>
            <h3>Least Enrolled</h3>
          </div>
          {leastEnrolledCourses.length > 0 ? (
            <div className="courses-list">
              {leastEnrolledCourses.map((course, i) => (
                <div key={course.id || i} className="course-item">
                  <div className="course-rank">{i + 1}</div>
                  <img
                    src={course.image_url || course.image || course2}
                    alt={course.title}
                  />
                  <div className="course-details">
                    <p className="course-title">{course.title}</p>
                    <span className="metric-badge low">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                      {course.enrollments || 0} enrolled
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No least enrolled courses available.</p>
            </div>
          )}
        </div>
      </div>

      {/* Additional Analytics Charts - Integrated M&E */}
      <>
        <div className="cards-grid">
          <div className="card" style={{ gridColumn: "span 2" }}>
            <div className="card-header-with-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3>User Activity Distribution</h3>
              <p
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}
              >
                Active: {meMetrics.activeUsers} ({meMetrics.activePercentage}%)
                | Inactive: {meMetrics.inactiveUsers} (
                {meMetrics.inactivePercentage}%)
              </p>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={[
                    { name: "Active", value: meMetrics.activeUsers },
                    { name: "Inactive", value: meMetrics.inactiveUsers },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#28a745" />
                  <Cell fill="#dc3545" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ gridColumn: "span 2" }}>
            <div className="card-header-with-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 13l4-4 4 4 8-8 2 2-10 10-4-4-2 2z"
                />
              </svg>
              <h3>Staff by LGA</h3>
              <p
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}
              >
                Distribution of registered FLWs by Local Government Area (Top 15)
              </p>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={meMetrics.staffLgaData || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip
                  formatter={(value) => [value, "Staff"]}
                  labelFormatter={(label, payload) =>
                    payload?.[0]?.payload?.fullName || label
                  }
                />
                <Bar dataKey="value" fill="#14b8a6" radius={[0, 4, 4, 0]}>
                  {(meMetrics.staffLgaData || []).map((entry, index) => (
                    <Cell
                      key={`cell-staff-${index}`}
                      fill={`hsl(${160 + index * 8}, 65%, 45%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ gridColumn: "span 2" }}>
            <div className="card-header-with-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3>Demographics</h3>
              <p
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}
              >
                Female: {meMetrics.demographics.female} (
                {meMetrics.demographics.femalePercentage}%) | Male:{" "}
                {meMetrics.demographics.male} (
                {meMetrics.demographics.malePercentage}%)
              </p>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={[
                    { name: "Female", value: meMetrics.demographics.female },
                    { name: "Male", value: meMetrics.demographics.male },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#ff6384" />
                  <Cell fill="#36a2eb" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ gridColumn: "span 2" }}>
            <div className="card-header-with-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3>Enrollments by LGA</h3>
              <p
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}
              >
                Distribution of enrolments by Local Government Area
              </p>
              <div style={{ marginTop: "10px" }}>
                <select
                  value={selectedLgaCourse}
                  onChange={(e) => setSelectedLgaCourse(e.target.value)}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    background: "#fff",
                    color: "#333",
                  }}
                >
                  <option value="">All courses</option>
                  {(courses || []).map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title || `Course #${course.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(400, (meMetrics.lgaData || []).length * 28)}>
              <BarChart data={meMetrics.lgaData || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value, name) => [value, "Enrolments"]}
                  labelFormatter={(label, payload) =>
                    payload?.[0]?.payload?.fullName || label
                  }
                />
                <Bar dataKey="value" fill="#F77600" radius={[0, 4, 4, 0]}>
                  {(meMetrics.lgaData || []).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`hsl(${200 + index * 10}, 70%, 50%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ gridColumn: "span 2" }}>
            <div className="card-header-with-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h3>Users by Cadre</h3>
              <p
                style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}
              >
                Distribution of users by job title/cadre
              </p>
            </div>
            <ResponsiveContainer width="100%" height={450}>
              <PieChart>
                <Pie
                  data={meMetrics.cadreData || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={140}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {meMetrics.cadreData?.map((entry, index) => {
                    const colors = [
                      "#10b981",
                      "#3b82f6",
                      "#f59e0b",
                      "#ef4444",
                      "#8b5cf6",
                      "#ec4899",
                      "#06b6d4",
                      "#84cc16",
                      "#f97316",
                      "#6366f1",
                      "#14b8a6",
                      "#a855f7",
                      "#f43f5e",
                      "#fb923c",
                    ];
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[index % colors.length]}
                      />
                    );
                  })}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Course Enrollment Table */}
        {/* {
          meMetrics.courseEnrollments.length > 0 && (
            <div className="card">
              <div className="card-header-with-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h3>Course Enrollment Statistics</h3>
              </div>
              <div className="table-wrapper">
                <table className="status-table">
                  <thead>
                    <tr>
                      <th>Course Name</th>
                      <th>Enrolled Users</th>
                      <th>% of Total</th>
                      <th>Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meMetrics.courseEnrollments.map((course, idx) => {
                      const isLowCompletion = course.completionRate < 60;
                      const isHighCompletion = course.completionRate >= 75;
                      return (
                        <tr key={idx} className={isLowCompletion ? "table-row" : isHighCompletion ? "table-row" : "table-row"}>
                          <td><strong>{course.name}</strong></td>
                          <td>{course.enrolled}</td>
                          <td>{course.percentageOfTotal}%</td>
                          <td>
                            <span className={`badge ${isLowCompletion ? "badge-danger" : isHighCompletion ? "badge-success" : "badge-warning"}`}>
                              {course.completionRate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        } */}
      </>
    </div>
  );
};

export default Dashboard;
